import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { NavigateOptions, useNavigate } from 'react-router-dom'
import { disableBlueTapHighlight } from '../common'

type Props = {
  hrefLink: string
  children: React.ReactNode
  target?: React.HTMLAttributeAnchorTarget
  testId?: string
  onNavCallback?: () => any
  search?: string
  navigateOptions?: NavigateOptions
}

/**
 * A wrapper for Anchor tags to allow React-Router navigation whilst still
 * remaining readable / useable as an Anchor tag for Web Crawlers and the like.
 *
 * @param hrefLink - Ensure hrefLinks start with a prefix of '/order' to have correct href value,
 * this prefix will be stripped for React Router navigation.
 */
const RouterAnchor = ({
  hrefLink,
  children,
  target,
  testId,
  onNavCallback,
  search,
  navigateOptions
}: Props) => {
  const navigate = useNavigate()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Allow default behavior for external URLs
    if (
      hrefLink.startsWith('http://') ||
      hrefLink.startsWith('https://') ||
      hrefLink.startsWith('//')
    ) {
      return
    }

    // Ignore clicks that include modifier keys or target="_blank"
    if (
      e.defaultPrevented ||
      e.metaKey ||
      e.altKey ||
      e.ctrlKey ||
      e.shiftKey ||
      target === '_blank'
    ) {
      return
    }

    let routerLink = hrefLink
    if (hrefLink.startsWith('/order'))
      routerLink = hrefLink.slice('/order'.length)
    if (routerLink.length === 0) routerLink = '/' // default link to home route
    e.preventDefault() // Prevent full page reload
    if (onNavCallback) onNavCallback()
    navigate(
      {
        pathname: routerLink,
        search: search
      },
      { ...navigateOptions }
    ) // Use React Router navigation
  }

  return (
    <StyledAnchor
      href={`${hrefLink}${search}`}
      onClick={handleClick}
      target={target}
      data-testid={testId}
    >
      {children}
    </StyledAnchor>
  )
}

/**
 * Make it styled to ensure no conflicting CSS.
 * Parent and children elements should handle all CSS.
 */
const StyledAnchor = styled.a<StyledHTMLElement>((theme) => ({
  width: '100%',
  height: '100%',
  textDecoration: 'none',
  color: 'inherit',
  lineHeight: 'inherit',
  font: 'inherit',
  background: 'none',
  border: 'none',
  ...disableBlueTapHighlight()
}))

export default RouterAnchor
